import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import EmojiPicker, { Theme as EmojiTheme, Categories as EmojiCategories } from "emoji-picker-react";
import type { ReactionSummary } from "../../types/domain";
import { useTheme } from "../../context/ThemeContext";
import { useIsMobile } from "../../hooks/useIsMobile";

const QUICK_REACTIONS = ["👍", "❤️", "🖕"] as const;

const CZ_EMOJI_CATEGORIES = [
  { category: EmojiCategories.SUGGESTED, name: "Naposledy použité" },
  { category: EmojiCategories.SMILEYS_PEOPLE, name: "Smajlíci a lidé" },
  { category: EmojiCategories.ANIMALS_NATURE, name: "Zvířata a příroda" },
  { category: EmojiCategories.FOOD_DRINK, name: "Jídlo a pití" },
  { category: EmojiCategories.TRAVEL_PLACES, name: "Cestování a místa" },
  { category: EmojiCategories.ACTIVITIES, name: "Aktivity" },
  { category: EmojiCategories.OBJECTS, name: "Předměty" },
  { category: EmojiCategories.SYMBOLS, name: "Symboly" },
  { category: EmojiCategories.FLAGS, name: "Vlajky" },
];

const PICKER_HEIGHT = 380;
const PICKER_MARGIN = 8;

interface ChatMessageReactionsProps {
  reactions: ReactionSummary[];
  canReact: boolean;
  onAddReaction: (emoji: string) => void;
  onRemoveReaction: (emoji: string) => void;
}

interface PickerPos {
  top: number;
  left: number;
}

function ChatMessageReactions({ reactions, canReact, onAddReaction, onRemoveReaction }: ChatMessageReactionsProps) {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const [pickerPos, setPickerPos] = useState<PickerPos | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const pickerWidth = isMobile ? 280 : 320;

  const openPicker = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;
    const placeAbove = spaceAbove >= PICKER_HEIGHT + PICKER_MARGIN || spaceAbove > spaceBelow;
    const top = placeAbove
      ? Math.max(PICKER_MARGIN, rect.top - PICKER_HEIGHT - PICKER_MARGIN)
      : Math.min(window.innerHeight - PICKER_HEIGHT - PICKER_MARGIN, rect.bottom + PICKER_MARGIN);
    let left = rect.left;
    if (left + pickerWidth > window.innerWidth - PICKER_MARGIN) {
      left = window.innerWidth - pickerWidth - PICKER_MARGIN;
    }
    if (left < PICKER_MARGIN) left = PICKER_MARGIN;
    setPickerPos({ top, left });
  };

  const closePicker = () => setPickerPos(null);

  useEffect(() => {
    if (!pickerPos) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        popoverRef.current && !popoverRef.current.contains(target) &&
        triggerRef.current && !triggerRef.current.contains(target)
      ) {
        closePicker();
      }
    };
    const onScroll = (e: Event) => {
      // Ignore scroll inside the picker itself (emoji list, category nav).
      const target = e.target as Node | null;
      if (target && popoverRef.current?.contains(target)) return;
      closePicker();
    };
    const onResize = () => closePicker();
    document.addEventListener("mousedown", onClick);
    // Capture-phase listener so nested scroll containers (like .chat-messages) close us too.
    document.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [pickerPos]);

  const toggle = (emoji: string, reactedByUser: boolean) => {
    if (reactedByUser) onRemoveReaction(emoji);
    else onAddReaction(emoji);
  };

  const reactionsByEmoji = new Map(reactions.map(r => [r.emoji, r]));

  return (
    <div className="chat-reactions">
      {reactions.map(r => (
        <OverlayTrigger
          key={r.emoji}
          placement="top"
          delay={{ show: 150, hide: 0 }}
          overlay={
            <Tooltip id={`chat-reaction-tip-${r.emoji}`} className="chat-reaction-tooltip">
              <div className="chat-reaction-tooltip-emoji">{r.emoji}</div>
              {r.users.length > 0 && (
                <div className="chat-reaction-tooltip-users">
                  {r.users.map((name, i) => (
                    <div key={i}>{name}</div>
                  ))}
                </div>
              )}
            </Tooltip>
          }
        >
          <button
            className={`chat-reaction-chip${r.reactedByUser ? " active" : ""}`}
            onClick={() => canReact && toggle(r.emoji, r.reactedByUser)}
            disabled={!canReact}
            aria-label={r.reactedByUser ? t("removeReaction") : t("addReaction")}
            type="button"
          >
            <span className="chat-reaction-emoji">{r.emoji}</span>
            <span className="chat-reaction-count">{r.count}</span>
          </button>
        </OverlayTrigger>
      ))}

      {canReact && (
        <>
          {QUICK_REACTIONS.map(emoji => {
            // If this emoji is already present as a chip (regardless of who reacted),
            // hide the quick button — the chip itself acts as the toggle.
            if (reactionsByEmoji.has(emoji)) return null;
            return (
              <button
                key={emoji}
                className="chat-reaction-quick"
                onClick={() => onAddReaction(emoji)}
                title={t("react") || "React"}
                type="button"
              >
                {emoji}
              </button>
            );
          })}

          <button
            ref={triggerRef}
            className="chat-reaction-picker-btn"
            onClick={() => (pickerPos ? closePicker() : openPicker())}
            title={t("emoji") || "Emoji"}
            aria-label={t("emoji") || "Emoji"}
            type="button"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <path d="M8 14s1.5 2 4 2 4-2 4-2" />
              <line x1="9" y1="9" x2="9.01" y2="9" />
              <line x1="15" y1="9" x2="15.01" y2="9" />
            </svg>
            <span className="chat-reaction-picker-plus">+</span>
          </button>

          {pickerPos && createPortal(
            <div
              ref={popoverRef}
              className="chat-reaction-picker-popover"
              style={{ top: pickerPos.top, left: pickerPos.left }}
            >
              <EmojiPicker
                theme={theme === "light" ? EmojiTheme.LIGHT : EmojiTheme.DARK}
                onEmojiClick={(emojiObj) => {
                  closePicker();
                  const existing = reactionsByEmoji.get(emojiObj.emoji);
                  toggle(emojiObj.emoji, !!existing?.reactedByUser);
                }}
                width={pickerWidth}
                height={PICKER_HEIGHT}
                lazyLoadEmojis
                categories={i18n.language === "cs" ? CZ_EMOJI_CATEGORIES : undefined}
                searchPlaceHolder={i18n.language === "cs" ? "Hledat…" : "Search…"}
                previewConfig={{ showPreview: false }}
              />
            </div>,
            document.body
          )}
        </>
      )}
    </div>
  );
}

export default ChatMessageReactions;
